/**
 * UserMenu — unified account dropdown.
 *
 * Merges login/logout + settings access + account status:
 * avatar, email, plan badge, points bar, CV count, settings link,
 * logout with confirmation.
 */

import { useState, useRef, useEffect } from 'react';
import { Settings2, LogOut, LogIn, Crown, Zap, ChevronDown, User, AlertCircle, Sun, Moon, Globe, Target } from 'lucide-react';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { getPointsSummary, PLAN_CONFIG, type Plan } from '../lib/credits-store';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';
import { useNavigate } from 'react-router-dom';

interface UserMenuProps {
  user: SupabaseUser | null;
  cvVersionCount: number;
  theme: 'light' | 'dark';
  locale: 'fr' | 'en';
  isAtsSidebarOpen: boolean;
  onThemeToggle: () => void;
  onLocaleToggle: () => void;
  onAtsSidebarToggle: () => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  onOpenSettings: () => void;
}

function Initials({ email }: { email: string }) {
  const parts = email.split('@')[0].split(/[._-]/);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
  return (
    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-600/40 text-violet-200 text-xs font-bold shrink-0 select-none">
      {letters || <User size={14} />}
    </span>
  );
}

export function UserMenu({ user, cvVersionCount, theme, locale, isAtsSidebarOpen, onThemeToggle, onLocaleToggle, onAtsSidebarToggle, onLoginClick, onLogoutClick, onOpenSettings }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const navigate = useNavigate();

  const summary = getPointsSummary();
  const plan = summary.plan;
  const planCfg = PLAN_CONFIG[plan];

  const PLAN_BADGE: Record<Plan, { label: string; className: string }> = {
    free:    { label: t('userMenu.planFree'), className: 'bg-white/10 text-white/50' },
    mini:    { label: t('userMenu.planMini'), className: 'bg-sky-500/20 text-sky-300 border border-sky-500/30' },
    premium: { label: t('userMenu.planPremium'), className: 'bg-amber-500/20 text-amber-300 border border-amber-500/30' },
  };
  const badge = PLAN_BADGE[plan];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirmLogout(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!user) {
    return (
      <button
        type="button"
        onClick={onLoginClick}
        className="hidden md:flex px-2.5 py-2 rounded-lg bg-blue-600/20 text-blue-400 hover:text-blue-300 hover:bg-blue-600/30 transition-all min-h-[44px] items-center gap-1.5 text-sm font-bold"
        aria-label="Connexion / Inscription"
      >
        <LogIn size={15} aria-hidden="true" />
        <span>{t('userMenu.login')}</span>
      </button>
    );
  }

  const email = user.email ?? 'Connecté';
  const truncated = email.length > 22 ? email.slice(0, 19) + '…' : email;

  const handleLogout = () => {
    if (confirmLogout) {
      onLogoutClick();
      setOpen(false);
      setConfirmLogout(false);
    } else {
      setConfirmLogout(true);
    }
  };

  return (
    <div ref={ref} className="relative hidden md:block">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => { setOpen((p) => !p); setConfirmLogout(false); }}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg min-h-[44px] transition-all text-sm font-bold',
          open
            ? 'bg-violet-600/20 text-violet-200'
            : 'bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30',
        )}
        aria-label="Mon compte"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Initials email={email} />
        <span className="max-w-[120px] truncate text-xs">{truncated}</span>
        <ChevronDown size={13} className={cn('shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          role="dialog"
          aria-label="Menu compte"
          className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-white/10 bg-[#161622] shadow-2xl shadow-black/60 z-50 overflow-hidden"
        >
          {/* Header section */}
          <div className="p-4 border-b border-white/8 flex items-start gap-3">
            <Initials email={email} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white/90 truncate">{email}</div>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/pricing'); }}
                className="flex items-center gap-2 mt-1 hover:opacity-80 transition-opacity text-left"
              >
                <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider', badge.className)}>
                  {badge.label}
                </span>
                {plan !== 'premium' && (
                  <span className="text-[10px] text-white/40">
                    {cvVersionCount} CV{cvVersionCount !== 1 ? 's' : ''} · {planCfg.cvVersionsMax !== null ? `${planCfg.cvVersionsMax} max` : 'illimité'}
                  </span>
                )}
                {plan === 'premium' && (
                  <span className="flex items-center gap-1 text-[10px] text-amber-400/70">
                    <Crown size={10} /> {t('userMenu.unlimited')}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Points section */}
          <div className="p-4 border-b border-white/8 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-white/70">
                <Zap size={13} className="text-yellow-400" aria-hidden />
                {t('userMenu.credits')}
              </div>
              {plan === 'premium' ? (
                <span className="text-xs text-amber-400 font-bold">{t('userMenu.unlimited')}</span>
              ) : (
                <span className="text-xs text-white/60 font-mono">
                  {summary.remaining} / {summary.totalAvailable} pts
                </span>
              )}
            </div>

            {plan !== 'premium' && summary.totalAvailable !== null && (
              <>
                <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      summary.usagePercent >= 90 ? 'bg-red-500' :
                      summary.usagePercent >= 70 ? 'bg-amber-500' :
                      'bg-violet-500',
                    )}
                    style={{ width: `${summary.usagePercent}%` }}
                  />
                </div>
                {summary.earned > 0 && (
                  <div className="text-[10px] text-emerald-400/80">
                    {t('userMenu.earnedThisMonth').replace('{pts}', String(summary.earned))}
                  </div>
                )}
                <div className="text-[10px] text-white/30">
                  {t('userMenu.renewsMonthly')}
                </div>
              </>
            )}

            {/* Cost legend */}
            <div className="pt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
              {([
                ['Analyse CV', 3],
                ['Comparaison', 4],
                ['Optimisation', 5],
                ['Lettre motiv.', 8],
                ['Export PDF', 5],
                ['Export DOCX', 1],
              ] as const).map(([label, cost]) => (
                <div key={label} className="flex items-center justify-between text-[10px] text-white/30">
                  <span>{label}</span>
                  <span className="font-mono text-white/40">{cost} pt{cost > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Preferences: theme, locale, legacy ATS */}
          <div className="p-2 border-b border-white/8 space-y-0.5">
            <div className="text-[10px] font-bold uppercase text-white/30 tracking-wider px-1 pb-1">{t('userMenu.preferences')}</div>
            <button
              type="button"
              onClick={() => { onThemeToggle(); }}
              className="flex items-center justify-between w-full px-3 py-2 rounded-xl text-xs font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors text-left"
            >
              <span className="flex items-center gap-2">
                {theme === 'dark' ? <Sun size={14} className="shrink-0 text-amber-400" /> : <Moon size={14} className="shrink-0 text-indigo-400" />}
                {t('userMenu.theme')}
              </span>
              <span className="text-[10px] text-white/40 font-mono">{theme === 'dark' ? t('theme.dark') : t('theme.light')}</span>
            </button>
            <button
              type="button"
              onClick={() => { onLocaleToggle(); }}
              className="flex items-center justify-between w-full px-3 py-2 rounded-xl text-xs font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors text-left"
            >
              <span className="flex items-center gap-2">
                <Globe size={14} className="shrink-0 text-cyan-400" />
                {t('userMenu.language')}
              </span>
              <span className="text-[10px] text-white/40 font-mono">{locale === 'fr' ? 'FR' : 'EN'}</span>
            </button>
            <button
              type="button"
              onClick={() => { onAtsSidebarToggle(); setOpen(false); }}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 rounded-xl text-xs font-medium transition-colors text-left',
                isAtsSidebarOpen
                  ? 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                  : 'text-white/50 hover:bg-white/5 hover:text-white/70',
              )}
            >
              <span className="flex items-center gap-2">
                <Target size={14} className={cn('shrink-0', isAtsSidebarOpen ? 'text-emerald-400' : 'text-white/40')} />
                {t('userMenu.legacy')}
              </span>
              {isAtsSidebarOpen && <span className="text-[10px] text-emerald-400/70 font-mono">ON</span>}
            </button>
          </div>

          {/* Actions */}
          <div className="p-2 space-y-0.5">
            {plan !== 'premium' && (
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/pricing'); }}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-xs font-bold text-amber-300 hover:bg-amber-500/10 transition-colors text-left"
              >
                <Crown size={14} className="shrink-0" />
                {plan === 'free' ? t('userMenu.upgradeFreeMini') : t('userMenu.upgradeMini')}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); onOpenSettings(); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-xs font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors text-left"
            >
              <Settings2 size={14} className="shrink-0" />
              {t('userMenu.settings')}
            </button>

            {/* Logout with confirmation */}
            {!confirmLogout ? (
              <button
                type="button"
                onClick={() => setConfirmLogout(true)}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-xs font-medium text-white/50 hover:bg-red-500/10 hover:text-red-300 transition-colors text-left"
              >
                <LogOut size={14} className="shrink-0" />
                {t('userMenu.logout')}
              </button>
            ) : (
              <div className="px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 space-y-2">
                <div className="flex items-center gap-1.5 text-[11px] text-red-300 font-medium">
                  <AlertCircle size={12} className="shrink-0" />
                  {t('userMenu.logoutConfirm')}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 text-[11px] font-bold hover:bg-red-500/30 transition-colors"
                  >
                    {t('userMenu.logoutConfirmBtn')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmLogout(false)}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-white/5 text-white/60 text-[11px] font-bold hover:bg-white/10 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
